import { useCallback, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { Button } from "simplydo/react/ui/button";
import { Divider } from "simplydo/react/ui/divider";
import { Form } from "simplydo/react/ui/form";
import { H2, H4, Subheader } from "simplydo/react/ui/header";
import { Input } from "simplydo/react/ui/input";
import { Message } from "simplydo/react/ui/message";
import { TextArea } from "simplydo/react/ui/textarea";

import api from "api";
import { useAppSelector } from "store";

type ReportContentProps = {
  forType?: string;
  forId?: string;
  reportUrl?: string;
  onComplete?: () => void;
};

const ReportContent = ({
  forType,
  forId,
  reportUrl,
  onComplete,
}: ReportContentProps) => {
  const [location, setLocation] = useState<string>(reportUrl ?? "");
  const [description, setDescription] = useState<string>("");
  const user = useAppSelector((state) => state.user);
  const [email, setEmail] = useState<string>(user?.emails[0]?.address ?? "");
  const { t } = useTranslation();

  const reportContent = useCallback(() => {
    api.auth.reportContent(
      { forType, forId, description, email, location },
      () => {
        toast.success("Report submitted successfully");
        setDescription("");
        setLocation("");
        onComplete?.();
      },
      () => {
        toast.error("Failed to submit report");
      },
    );
  }, [forType, forId, description, email, location, onComplete]);

  return (
    <div>
      <H2>
        {t("reportContent.title")}
        <Subheader>
          <p>{t("reportContent.onlineSafety")}</p>
          <p>{t("reportContent.contentModeration")}</p>
          <p>{t("reportContent.reportReviews")}</p>
        </Subheader>
      </H2>
      <H4>
        {t("reportContent.contentTitle")}
        <Subheader>{t("reportContent.reportLocationMessage")}</Subheader>
      </H4>
      <Input
        placeholder="https://simplydo.co.uk"
        value={location}
        fluid
        onChange={(e) => setLocation(e.target.value)}
      />
      <H4>
        {t("reportContent.describeIssueTitle")}
        <Subheader>{t("reportContent.describeIssueContent")}</Subheader>
      </H4>
      <TextArea
        placeholder="Please provide as much detail as possible."
        onChange={(e) => setDescription(e.target.value)}
        value={description}
      />
      <H4>
        {t("reportContent.contactTitle")}
        <Subheader>{t("reportContent.contactDescription")}</Subheader>
      </H4>
      <Input
        fluid
        placeholder={t("placeholder.email")}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <Message>
        {t("reportContent.privacyPolicyMessage")}{" "}
        <a
          target="_blank"
          rel="noopener noreferrer"
          href="https://simplydo.co.uk/privacy-policy"
        >
          {t("reportContent.privacyPolicy")}
        </a>
        .
      </Message>
      <div style={{ textAlign: "right" }}>
        <Button onClick={() => onComplete?.()}>{t("generic.cancel")}</Button>
        <Button onClick={() => reportContent()} color="primary">
          {t("reportContent.submit")}
        </Button>
      </div>
    </div>
  );
};

export default ReportContent;
